// src/pages/BhaktiHome.jsx
import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";

const faqData = {
  "Bhakt (Devotee)": [
    // Negative
    {
      sentiment: "negative",
      question: "I don’t have time for long spiritual content. Will this really help me?",
      answer:
        "Bhakti keeps everything simple and bite-sized — short chants, quick learnings, daily inspirations, and small self-actions. Even 2 minutes a day deepens your devotion and inner balance.",
    },
    {
      sentiment: "negative",
      question: "Why should I use Bhakti when I can directly watch videos on YouTube?",
      answer:
        "YouTube is general. Bhakti is guided: structured learnings, daily sadhana, tasks from Guruji, Bhakti Diary, and personalized spiritual nudges — all in one place.",
    },
    {
      sentiment: "negative",
      question: "I am not very tech-savvy. Will I understand this app?",
      answer:
        "Bhakti is designed for all age groups — large icons, voice-enabled navigation, simple steps, and zero complexity. You don’t need technical knowledge to use it.",
    },
    {
      sentiment: "negative",
      question: "Is this another social media distraction?",
      answer:
        "No. Bhakti is **anti-distraction** — no gossip, no noise. Only devotion, daily growth, and purposeful spiritual engagement.",
    },

    // Neutral
    {
      sentiment: "neutral",
      question: "What all can I do daily on Bhakti?",
      answer:
        "You can chant, listen to Guruji’s Pravachan, complete your daily task, write in Bhakti Diary, read short learnings, and watch BhaktiTube content.",
    },
    {
      sentiment: "neutral",
      question: "Will there be new content every day?",
      answer:
        "Yes. Daily chants, learnings, Pravachan, tasks, reflections, rankings, and rewards are updated regularly.",
    },
    {
      sentiment: "neutral",
      question: "Is Bhakti free to use?",
      answer:
        "Core features like chants, learnings, and diary are free. Some advanced modules or events may have optional fees.",
    },

    // Positive
    {
      sentiment: "positive",
      question: "Can Bhakti help me build a daily spiritual habit?",
      answer:
        "Absolutely. Your Bhakti streak, reminders, personalized tasks, and rewards make devotion easy, enjoyable, and consistent.",
    },
    {
      sentiment: "positive",
      question: "Does Bhakti offer personalized spiritual guidance?",
      answer:
        "Yes. Bhakti AI and Guruji’s tasks gently guide your daily actions, reflections, and learning journey.",
    },
    {
      sentiment: "positive",
      question: "Can I grow spiritually even if I practice just 5 minutes a day?",
      answer:
        "Yes. Consistency matters more than duration. Bhakti nudges, small tasks, and daily reflections help deepen devotion effortlessly.",
    },
  ],

  "Sevak / Volunteer": [
    // Negative
    {
      sentiment: "negative",
      question: "Will Bhakti add more workload to our seva?",
      answer:
        "Not at all. Bhakti reduces manual coordination, simplifies communication, and gives you clear daily updates and tools.",
    },
    // Neutral
    {
      sentiment: "neutral",
      question: "Can Bhakti help us manage devotees better?",
      answer:
        "Yes. You get engagement insights, event lists, volunteer tasks, and participation data in real-time.",
    },
    // Positive
    {
      sentiment: "positive",
      question: "Will Bhakti improve community involvement?",
      answer:
        "Yes. Daily tasks, rewards, sadhana tracking, and BhaktiTube keep devotees active, inspired, and connected.",
    },
  ],

  "Guruji’s Team": [
    // Negative
    {
      sentiment: "negative",
      question: "Will devotees take tasks seriously?",
      answer:
        "Bhakti gamifies devotion — streaks, rewards, rankings, reflections, and community challenges ensure daily participation.",
    },
    // Neutral
    {
      sentiment: "neutral",
      question: "Can we publish Pravachan, tasks, and learnings easily?",
      answer:
        "Yes. The platform supports scheduled content, audio, video, text, and image uploads effortlessly.",
    },
    // Positive
    {
      sentiment: "positive",
      question: "Can Bhakti help deepen the connection between Guruji and devotees?",
      answer:
        "Absolutely. Daily tasks, personalized messages, and guided sadhana strengthen the spiritual bridge.",
    },
  ],

  "Event Organizers": [
    {
      sentiment: "neutral",
      question: "Can we promote our events through Bhakti?",
      answer:
        "Yes. Add event details, timings, and locations, and devotees can register instantly.",
    },
    {
      sentiment: "positive",
      question: "Will Bhakti help increase participation?",
      answer:
        "Daily notifications, reminders, and rewards boost event attendance significantly.",
    },
  ],

  "Content Creators (BhaktiTube)": [
    {
      sentiment: "neutral",
      question: "What type of content performs best on BhaktiTube?",
      answer:
        "Chants, Pravachans, Stories, Wisdom shorts, spiritual life hacks, and meditative audios perform exceptionally well.",
    },
    {
      sentiment: "positive",
      question: "Can creators earn through Bhakti?",
      answer:
        "Yes. Through Rewarding Ads, ValueShare, and community bonuses.",
    },
  ],

  "Marketplace Partners": [
    {
      sentiment: "neutral",
      question: "What products can we list?",
      answer:
        "Pooja items, books, spiritual souvenirs, eco-friendly products, meditation tools, and devotional art.",
    },
    {
      sentiment: "positive",
      question: "Will Bhakti increase trust in sellers?",
      answer:
        "Yes. Ratings, reviews, authenticity checks, and curated listings enhance credibility.",
    },
  ],

  "Bhakti AI": [
    {
      sentiment: "neutral",
      question: "What can Bhakti AI actually do?",
      answer:
        "It provides personalized sadhana suggestions, mood-based chants, reflective prompts, and content recommendations.",
    },
    {
      sentiment: "positive",
      question: "Does Bhakti AI help deepen my spiritual practice?",
      answer:
        "Yes. It learns from your daily habits, reflections, chants, and watch history to guide your spiritual growth lovingly.",
    },
  ],
};

const BhaktiFAQHome = () => {
  const [openSections, setOpenSections] = useState({}); 

  const toggleSection = (sectionKey, itemIndex) => {
    setOpenSections((prev) => ({
      ...prev,
      [`${sectionKey}-${itemIndex}`]: !prev[`${sectionKey}-${itemIndex}`],
    }));
  };

  const getSentimentClasses = (sentiment) => {
    switch (sentiment) {
      case "negative":
        return "bg-red-950/20 dark:bg-red-900/20 border-red-900/30 dark:border-red-800/30";
      case "positive":
        return "bg-orange-950/20 dark:bg-orange-900/20 border-orange-900/30 dark:border-orange-800/30";
      case "neutral":
      default:
        return "bg-gray-950/20 dark:bg-gray-900/20 border-gray-900/30 dark:border-gray-800/30";
    }
  };

  return (
    <div className="mx-auto p-5 font-sans bg-black text-white min-h-screen">
      <h1 className="text-center text-3xl font-bold text-orange-400 mb-4">
        Welcome to Bhakti
      </h1>
      <p className="text-center text-gray-300 mb-8">
        Explore frequently asked questions tailored to your role in our spiritual community.
      </p>

      {Object.entries(faqData).map(([sectionTitle, items]) => (
        <div
          key={sectionTitle}
          className="mb-8 border border-gray-800 rounded-lg overflow-hidden bg-gray-900"
        >
          <h2 className="bg-gray-800 p-4 m-0 border-b border-gray-700 text-xl font-semibold text-white">
            {sectionTitle}
          </h2>
          <div>
            {items.map((item, index) => (
              <div
                key={index}
                className={`border-b ${getSentimentClasses(item.sentiment)}`}
              >
                <button
                  onClick={() => toggleSection(sectionTitle, index)}
                  className="w-full p-4 border-none bg-transparent text-left cursor-pointer text-lg font-bold text-white hover:text-orange-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-opacity-50"
                >
                  <span>Q: {item.question}</span>
                  <span className="float-right text-2xl">
                    {openSections[`${sectionTitle}-${index}`] ? "−" : "+"}
                  </span>
                </button>
                {openSections[`${sectionTitle}-${index}`] && (
                  <div className="p-4 pt-0 first:pt-4">
                    <p className="m-0 text-gray-200 leading-relaxed text-base">
                      A: {item.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default BhaktiFAQHome;